/**
 * Analytics bootstrap — GA4, Meta Pixel, TikTok Pixel.
 *
 * All trackers are gated on Vite env vars. If an env var is absent the
 * corresponding tracker is never loaded. Google Consent Mode v2 defaults to
 * "denied" so nothing is actually sent until CookieConsentBanner grants
 * analytics consent (see `updateAnalyticsConsent`).
 */

import { hasConsentFor } from '@/utils/cookieConsent';

type ConsentState = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    ttq?: {
      track: (event: string, params?: Record<string, unknown>) => void;
      page: () => void;
      load: (id: string) => void;
      instance: (id: string) => { track: (event: string) => void };
      [key: string]: unknown;
    };
  }
}

const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const TIKTOK_PIXEL_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID as string | undefined;
const GSC_VERIFICATION = import.meta.env.VITE_GSC_VERIFICATION as string | undefined;

let initialized = false;

function injectGscVerification(): void {
  if (!GSC_VERIFICATION) return;
  if (document.querySelector('meta[name="google-site-verification"]')) return;
  const meta = document.createElement('meta');
  meta.name = 'google-site-verification';
  meta.content = GSC_VERIFICATION;
  document.head.appendChild(meta);
}

function loadGA4(): void {
  if (!GA4_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };

  // Consent Mode v2 — default everything to denied until user consents
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });

  window.gtag('js', new Date());
  window.gtag('config', GA4_ID, {
    anonymize_ip: true,
    send_page_view: false, // page views fire via useLandingAnalytics hook
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);
}

function loadMetaPixel(): void {
  if (!META_PIXEL_ID) return;

  // Standard Meta Pixel snippet, inlined so we can gate on env var
  /* eslint-disable */
  // @ts-ignore
  !(function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  // Default to revoked; flip to 'grant' once analytics consent granted
  window.fbq!('consent', 'revoke');
  window.fbq!('init', META_PIXEL_ID);
  window.fbq!('track', 'PageView');
}

function loadTikTokPixel(): void {
  if (!TIKTOK_PIXEL_ID) return;

  /* eslint-disable */
  // @ts-ignore
  !(function (w: any, d: any, t: any) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = [
      'page', 'track', 'identify', 'instances', 'debug', 'on', 'off',
      'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
    ];
    ttq.setAndDefer = function (t: any, e: any) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t: any) {
      const e = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e: any, n: any) {
      const r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const o = d.createElement('script');
      o.type = 'text/javascript';
      o.async = !0;
      o.src = r + '?sdkid=' + e + '&lib=' + t;
      const a = d.getElementsByTagName('script')[0];
      a.parentNode.insertBefore(o, a);
    };
    ttq.load(TIKTOK_PIXEL_ID);
    ttq.page();
  })(window, document, 'ttq');
  /* eslint-enable */
}

/**
 * Initialize all analytics trackers. Call once, early, before React mounts.
 * No-op if called more than once.
 */
export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  injectGscVerification();
  loadGA4();
  loadMetaPixel();
  loadTikTokPixel();

  // If user already consented in a prior session, apply immediately.
  if (hasConsentFor('analytics')) {
    updateAnalyticsConsent('granted');
  }
}

/**
 * Flip consent state for all loaded trackers. Call from CookieConsentBanner
 * whenever the user accepts / declines / changes preferences.
 */
export function updateAnalyticsConsent(state: ConsentState): void {
  if (typeof window === 'undefined') return;

  if (window.gtag) {
    window.gtag('consent', 'update', {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state,
    });
  }

  if (window.fbq) {
    window.fbq('consent', state === 'granted' ? 'grant' : 'revoke');
  }

  // TikTok follows its own cookie API
  if (window.ttq) {
    if (state === 'granted') {
      (window.ttq as { enableCookie?: () => void }).enableCookie?.();
    } else {
      (window.ttq as { disableCookie?: () => void }).disableCookie?.();
    }
  }
}
